// Actual domain logic should be in a domainLogic.js file, at the appropriate
// level in the hierarchy.
import { Loading } from "classes/Loading";

import { removeLotAndSerialValues } from "services/sosInventoryService/domainLogic";
import { updateLineItemSerials } from "services/sosInventoryService/domainLogic";
import { updateBuildLineItems } from "services/sosInventoryService/productionTransaction/domainLogic";
import { getRecord } from "services/sosInventoryService/sosApi";
import { setBinForInboundLines } from "services/utility/bins";
import { handleProgramError } from "services/utility/errors";

import globalState from "globalState/globalState";
import { linesLoadingOn, linesLoadingOff } from "globalState/loadingSlice";

export async function updateTransaction(
  field,
  newValue,
  currentState,
  updaters
) {
  const { record, outputLines, inputLines, relatedRecords } = currentState;
  if (!record || !outputLines || !inputLines || !relatedRecords) {
    handleProgramError(new Error("update Transaction | invalid currentState"));
  }

  let newRecord, newOutputs, newInputs, newRelatedRecords;

  switch (field) {
    case "date":
      newRecord = { ...record, date: newValue };
      updaters.setRecord(newRecord);
      if (newValue) {
        globalState.dispatch(linesLoadingOn());
        // show onhand skeleton on both handlers while async data resolves
        updaters.outputLineHandler({
          type: "setFields",
          fields: { onhand: new Loading(), onSO: new Loading() },
        });
        updaters.inputLineHandler({
          type: "setFields",
          fields: { onhand: new Loading() },
        });

        try {
          newOutputs = await updateBuildLineItems(
            newValue,
            record.location,
            outputLines,
            "output"
          );
          newInputs = await updateBuildLineItems(
            newValue,
            record.location,
            inputLines,
            "input"
          );
          // patch only computed fields; preserves any user edits made while async work was in flight
          updaters.outputLineHandler({
            type: "updateFields",
            lines: newOutputs,
            fields: ["relatedRecords", "onhand", "uom", "onSO"],
          });
          updaters.inputLineHandler({
            type: "updateFields",
            lines: newInputs,
            fields: ["relatedRecords", "onhand", "uom", "bin", "availableBins"],
          });
        } catch (e) {
          handleProgramError(e);
        } finally {
          globalState.dispatch(linesLoadingOff());
        }
      }

      break;

    case "location":
      globalState.dispatch(linesLoadingOn());
      // show onhand skeleton on both handlers while async data resolves
      updaters.outputLineHandler({
        type: "setFields",
        fields: { onhand: new Loading(), onSO: new Loading() },
      });
      updaters.inputLineHandler({
        type: "setFields",
        fields: { onhand: new Loading() },
      });
      try {
        newRecord = { ...record, location: newValue };
        updaters.setRecord(newRecord);
        const location = newValue
          ? await getRecord("location", newValue.id)
          : null;
        newRelatedRecords = { ...relatedRecords, location };

        newOutputs = await updateBuildLineItems(
          record.date,
          newValue,
          outputLines,
          "output"
        );
        newOutputs = await setBinForInboundLines(newOutputs, location);
        newInputs = await updateBuildLineItems(
          record.date,
          newValue,
          inputLines,
          "input"
        );
        newInputs = removeLotAndSerialValues(newInputs);
        // patch only computed fields; preserves any user edits made while async work was in flight
        updaters.outputLineHandler({
          type: "updateFields",
          lines: newOutputs,
          fields: ["relatedRecords", "onhand", "uom", "bin", "onSO"],
        });
        updaters.inputLineHandler({
          type: "updateFields",
          lines: newInputs,
          fields: [
            "relatedRecords",
            "onhand",
            "uom",
            "availableBins",
            "bin",
            "lot",
            "serials",
          ],
        });
      } catch (e) {
        handleProgramError(e);
      } finally {
        globalState.dispatch(linesLoadingOff());
      }

      break;

    case "job":
    case "comment":
    case "department":
    case "number":
    case "customFields":
    case "workcenter":
      newRecord = { ...record, [field]: newValue };
      updaters.setRecord(newRecord);

      break;

    case "autoSerialLotNumbering":
      newRecord = { ...record, [field]: newValue };
      updaters.setRecord(newRecord);
      newOutputs = updateLineItemSerials(newValue, outputLines);
      updaters.outputLineHandler({ type: "set", lines: newOutputs });
      break;

    default:
      handleProgramError(
        new Error(`updateTransaction | unknown field (${field})`)
      );
  }

  if (newRelatedRecords) {
    updaters.setRelatedRecords(newRelatedRecords);
  }
}
